import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listUserRefs, linkUserRef } from '../api/time-master-data.api';
import { ModeBanner } from '../components/ModeBanner';
import { Modal } from '@symphenia/ui-components';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import { usersApi } from '@/lib/users/users.api';
import type { TimeUserRef, PaginatedResponse } from '../types/time-master-data.types';
import type { User } from '@/lib/users/user.types';
import { hinweis } from '@/lib/dialoge';

/**
 * AP-09 — Read-only Liste der TimeUserRef-Datensätze + Typeahead-basiertes
 * Verknüpfungs-Dialog (AP-09-C).
 *
 * Die Verknüpfung TimeUserRef ↔ Symphenia-User erfolgt IMMER manuell durch einen Admin —
 * niemals automatisch.
 */
export default function TimeUserRefsPage() {
  const [search, setSearch] = useState('');
  const [linkedFilter, setLinkedFilter] = useState<'all' | 'true' | 'false'>('all');
  const [data, setData] = useState<PaginatedResponse<TimeUserRef> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ ref: TimeUserRef } | null>(null);

  const { canEdit } = useDataSourceMode('userRef');

  const reload = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    listUserRefs({
      search: search || undefined,
      linked: linkedFilter === 'all' ? undefined : linkedFilter,
      page: 1,
      limit: 50,
    })
      .then(res => { if (!cancelled) { setData(res); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.message ?? 'Fehler beim Laden'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, linkedFilter]);

  useEffect(() => {
    return reload();
  }, [reload]);

  const rows = useMemo(() => data?.data ?? [], [data]);

  async function submitLink(refId: string, userId: string | null) {
    setPendingId(refId);
    try {
      await linkUserRef(refId, userId);
      setDialog(null);
      reload();
    } catch (e: any) {
      void hinweis(e?.response?.data?.message ?? e?.message ?? 'Fehler beim Verknüpfen');
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mitarbeiter-Referenzen (Zeiterfassung)</h1>
          <p className="text-sm text-muted-foreground">
            Mitarbeiter aus deinem anderen Programm.
            Die Verknüpfung zu einem Symphenia-Konto stellst du immer selbst her.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={linkedFilter}
            onChange={e => setLinkedFilter(e.target.value as 'all' | 'true' | 'false')}
            className="border rounded px-2 py-2 text-sm"
          >
            <option value="all">Alle</option>
            <option value="true">Verknüpft</option>
            <option value="false">Nicht verknüpft</option>
          </select>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suchen (Name, E-Mail, ID)"
            className="border rounded px-3 py-2 text-sm w-72"
          />
          {canEdit ? (
            <Link
              to="/time-tracking/users/new"
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90"
            >
              + Neu
            </Link>
          ) : (
            <button
              type="button"
              disabled
              title="Nicht verfügbar, weil diese Daten aus deinem anderen Programm kommen."
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground opacity-40 cursor-not-allowed"
            >
              + Neu
            </button>
          )}
        </div>
      </header>

      <ModeBanner kind="userRef" />

      {loading && <div className="text-sm text-muted-foreground">Wird geladen …</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">E-Mail</th>
                <th className="text-left px-3 py-2">Quelle</th>
                <th className="text-left px-3 py-2">Verknüpfung</th>
                <th className="text-right px-3 py-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center px-3 py-6 text-muted-foreground">
                    Keine Datensätze.
                  </td>
                </tr>
              )}
              {rows.map(r => {
                const fullName = [r.firstName, r.lastName].filter(Boolean).join(' ') || '–';
                return (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2">{fullName}</td>
                    <td className="px-3 py-2">{r.email ?? '–'}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.externalSource
                        ? <span title={r.externalId ?? ''}>{r.externalSource} · {r.externalId}</span>
                        : <span className="text-muted-foreground">manuell</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.user
                        ? <span className="text-green-700">
                            <span className="font-mono">{r.user.email}</span>
                            <span className="ml-1 text-muted-foreground">
                              ({r.user.roles.map((x) => x.name).join(', ')})
                            </span>
                          </span>
                        : <span className="text-muted-foreground">nicht verknüpft</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/time-tracking/users/${r.id}/edit`}
                          className="text-xs border rounded px-2 py-1 hover:bg-accent"
                        >
                          Bearbeiten
                        </Link>
                        <button
                          type="button"
                          onClick={() => setDialog({ ref: r })}
                          disabled={pendingId === r.id}
                          className="text-xs border rounded px-2 py-1 hover:bg-accent disabled:opacity-50"
                        >
                          {pendingId === r.id
                            ? '…'
                            : r.userId
                              ? 'Verknüpfung ändern'
                              : 'Verknüpfen'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="text-xs text-muted-foreground">
          Seite {data.meta.page} / {data.meta.totalPages} – insgesamt {data.meta.total} Einträge
        </div>
      )}

      {dialog && (
        <LinkUserDialog
          refRow={dialog.ref}
          pending={pendingId === dialog.ref.id}
          onClose={() => setDialog(null)}
          onSave={(userId) => submitLink(dialog.ref.id, userId)}
        />
      )}
    </div>
  );
}

// ─── Verknüpfungs-Dialog mit Typeahead-User-Suche ───────────────────────────

function LinkUserDialog({
  refRow,
  pending,
  onClose,
  onSave,
}: {
  refRow: TimeUserRef;
  pending: boolean;
  onClose: () => void;
  onSave: (userId: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<User | null>(refRow.user
    ? ({ id: refRow.user.id, email: refRow.user.email, roles: refRow.user.roles } as User)
    : null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setSearchError(null);
      try {
        const res = await usersApi.list({ search: query.trim(), limit: 10, page: 1, kind: 'EMPLOYEE' });
        setResults(res.data.data);
      } catch (e: any) {
        setSearchError(e?.response?.data?.message ?? e?.message ?? 'Fehler bei der Suche');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const refName = [refRow.firstName, refRow.lastName].filter(Boolean).join(' ') || refRow.email || refRow.id;

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-lg" padded={false} hideCloseButton>
      <div className="p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Mitarbeiter verknüpfen</h2>
          <p className="text-sm text-muted-foreground">
            Mitarbeiter <span className="font-medium">{refName}</span>{' '}
            {refRow.externalSource && (
              <span className="text-xs">
                (Quelle: {refRow.externalSource} · {refRow.externalId})
              </span>
            )}
          </p>
        </div>

        {selected ? (
          <div className="flex items-center justify-between border rounded p-3 bg-accent/30">
            <div>
              <div className="text-sm font-medium">
                {selected.profile?.firstName} {selected.profile?.lastName}
                {!selected.profile?.firstName && !selected.profile?.lastName && selected.email}
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="font-mono">{selected.email}</span>
                {selected.roles?.length ? (
                  <span className="ml-2">({selected.roles.map((r) => r.name).join(', ')})</span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-xs border rounded px-2 py-1 hover:bg-accent"
            >
              Auswahl aufheben
            </button>
          </div>
        ) : (
          <div>
            <label className="block text-sm mb-1">Symphenia-Konto suchen (Name oder E-Mail)</label>
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="z.B. Max Muster oder m.muster@…"
              autoFocus
              className="w-full border rounded px-3 py-2 text-sm"
            />
            {query.length > 0 && query.length < 2 && (
              <p className="text-xs text-muted-foreground mt-1">Mindestens 2 Zeichen eingeben.</p>
            )}
            {searchError && (
              <p className="text-xs text-red-600 mt-1">{searchError}</p>
            )}
            {loading && (
              <p className="text-xs text-muted-foreground mt-1">Suche läuft …</p>
            )}
            {!loading && query.trim().length >= 2 && results.length === 0 && !searchError && (
              <p className="text-xs text-muted-foreground mt-1">Keine Treffer.</p>
            )}
            {results.length > 0 && (
              <ul className="mt-2 border rounded divide-y max-h-64 overflow-y-auto">
                {results.map(u => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(u)}
                      className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                    >
                      <div className="font-medium">
                        {u.profile?.firstName} {u.profile?.lastName}
                        {!u.profile?.firstName && !u.profile?.lastName && u.email}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-mono">{u.email}</span>
                        <span className="ml-2">({u.roles.map((r) => r.name).join(', ')})</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <button
            type="button"
            onClick={() => onSave(null)}
            disabled={pending || !refRow.userId}
            className="text-xs text-red-700 border border-red-200 rounded px-3 py-1.5 hover:bg-red-50 disabled:opacity-40"
            title="Vorhandene Verknüpfung lösen"
          >
            Verknüpfung lösen
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="text-sm border rounded px-3 py-1.5 hover:bg-accent disabled:opacity-40"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => selected && onSave(selected.id)}
              disabled={pending || !selected || selected.id === refRow.userId}
              className="text-sm bg-primary text-primary-foreground rounded px-3 py-1.5 disabled:opacity-50"
            >
              {pending ? 'Wird gespeichert …' : 'Verknüpfen'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
